import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
}) => {
    const [unitOfMeasurePosition, setUnitOfMeasurePosition] = useState(0);

    // Verndale Customization: Add data attributes for unit of measure
    const textToDisplay = `${cartLine.qtyOrdered!.toString()} ${cartLine.unitOfMeasureDisplay}`;

    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    // Verndale Customization: Add data attributes for unit of measure
    const getTextWidth = (text: string, font: string): number => {
        // re-use canvas object for better performance
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d") as CanvasRenderingContext2D;
        context!.font = font;
        const metrics = context!.measureText(text);
        return metrics.width;
    };

    useEffect(() => {
        if (value) {
            setUnitOfMeasurePosition(getTextWidth(value, "16px myriad-pro, sans-serif"));
        }
    }, [value]);
    // END Verndale Customization

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <StyledWrapper style={{ position: "relative" }}>
                <TextField
                    {...styles.editableQuantityTextField}
                    type="number"
                    min={0}
                    max={999999}
                    label={label || translate("QTY")}
                    value={value}
                    disabled={
                        !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]
                    }
                    onChange={changeHandler}
                    onKeyDown={keyDownHandler}
                    onBlur={blurHandler}
                    onClick={event => event.currentTarget.focus()}
                    data-test-selector="cartline_qty"
                    // Verndale Customization: Add data attributes for unit of measure
                    data-unit-of-measure={cartLine.unitOfMeasure}
                    onKeyUp={event => {
                        if (event.currentTarget.value.length > 0) {
                            setUnitOfMeasurePosition(
                                getTextWidth(event.currentTarget.value, "16px myriad-pro, sans-serif"),
                            );
                        }
                    }}
                    // END Verndale Customization
                />
                <span
                    className="customUOM"
                    style={{
                        position: "absolute",
                        top: "65%",
                        left: `${unitOfMeasurePosition + 15}px`,
                        fontSize: "12px",
                    }}
                >
                    {cartLine.unitOfMeasure}
                </span>
            </StyledWrapper>
        );
    }

    return (
        <SmallHeadingAndText
            heading={label || translate("QTY")}
            text={textToDisplay}
            data-test-selector="quoteDetail_cardLine_qty"
            // Verndale Customization: Add extendedStyles for unit of measure
            extendedStyles={styles.readOnlyQuantityText}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
